/* The maturity table — one page per object type.

   Services and assets were one table with a Show filter until it became clear
   the two do not belong in one grid. Services carry seven dimensions and
   assets three, so every asset row spent four columns saying "not applicable",
   which is four columns of nothing on a table that was already too wide. The
   two also answer different questions: services are read as a go-to-market
   portfolio, assets as the ground it stands on.

   So: `#/` is services, `#/assets` is assets, and this component is written
   once against whichever it was given. The dimension set, the class list and
   the column count all follow from `kind`.

   The table is read, not edited. Scores are set on an object's own page, where
   the dimension is named in full and the person changing it can see what else
   they are changing; twelve dense columns of click-to-edit mostly produced
   accidental edits.

   The stage filter starts with Idea switched off. Most of the portfolio sits
   in Idea, and a view that opens on it buries the things actually in motion.
   The filter button says "5 of 6" in accent colour whenever it is hiding rows,
   so the omission is visible rather than a trap. */
import { useEffect, useMemo, useState } from "react";

import { type Kind, type TrackedObject } from "../api";
import {
  Chip,
  DimensionIcon,
  MultiSelect,
  ScaleDialog,
  Score,
  hintFor,
  priorityTone,
  scoreStyle,
} from "../components/ui";
import { BetChips, InitiativeChip, NO_INITIATIVE } from "../components/strategy";
import { usePermissions } from "../auth";
import { href } from "../route";
import { useStore } from "../store";

type SortKey = "name" | "class" | "lifecycle_stage" | "priority" | "owner" | "maturity";

const PRIORITY_ORDER: Record<string, number> = { High: 0, Medium: 1, Low: 2 };

export default function Portfolio({ kind }: { kind: Kind }) {
  const { meta, services, assets, bets, initiatives } = useStore();
  const { canWrite } = usePermissions();
  const [initiative, setInitiative] = useState("");
  const [bet, setBet] = useState("");
  const [owner, setOwner] = useState("");
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState<SortKey>("maturity");
  const [scale, setScale] = useState<string | null>(null);

  /* null until the vocabulary arrives, and treated as "no filter" while it is.
     The defaults cannot be initial state because the stages and classes are
     served by the API, not compiled in. */
  const [klass, setKlass] = useState<string[] | null>(null);
  const [stage, setStage] = useState<string[] | null>(null);
  const [priority, setPriority] = useState<string[] | null>(null);

  const isService = kind === "service";
  const pool = isService ? services : assets;
  const dimensions = (isService ? meta?.service_dimensions : meta?.asset_dimensions) ?? [];
  const classes = (isService ? meta?.service_classes : meta?.asset_classes) ?? [];

  useEffect(() => {
    if (!meta) return;
    setKlass((prev) => prev ?? [...classes]);
    setStage((prev) => prev ?? meta.lifecycle_stages.filter((s) => s !== "Idea"));
    setPriority((prev) => prev ?? [...meta.priorities]);
    // `classes` is derived from meta and kind, both of which are in the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta, kind]);

  const betsOf = useMemo(
    () => new Map(initiatives.map((i) => [i.id, i.bet_ids])),
    [initiatives],
  );

  const rows = useMemo(() => {
    const filtered = pool.filter((o) => {
      if (stage !== null && !stage.includes(o.lifecycle_stage)) return false;
      if (klass !== null && !klass.includes(o.class)) return false;
      if (priority !== null && !priority.includes(o.priority)) return false;
      if (owner && (owner === "unassigned" ? !!o.owner_name : o.owner_name !== owner))
        return false;
      if (query && !o.name.toLowerCase().includes(query.toLowerCase())) return false;
      if (initiative) {
        if (initiative === NO_INITIATIVE) {
          if (o.initiative_id !== null) return false;
        } else if (o.initiative_id !== Number(initiative)) {
          return false;
        }
      }
      if (bet) {
        const ids = o.initiative_id ? (betsOf.get(o.initiative_id) ?? []) : [];
        if (bet === NO_INITIATIVE ? ids.length > 0 : !ids.includes(Number(bet))) return false;
      }
      return true;
    });
    const compare = (a: TrackedObject, b: TrackedObject) => {
      switch (sort) {
        case "maturity":
          /* Unscored objects sort last rather than as zero: they are not the
             least mature, they are the least known. */
          return (b.maturity.score ?? -1) - (a.maturity.score ?? -1) || a.name.localeCompare(b.name);
        case "priority":
          return (
            (PRIORITY_ORDER[a.priority] ?? 9) - (PRIORITY_ORDER[b.priority] ?? 9) ||
            a.name.localeCompare(b.name)
          );
        case "owner":
          return (a.owner_name ?? "zzz").localeCompare(b.owner_name ?? "zzz");
        case "class":
          return a.class.localeCompare(b.class) || a.name.localeCompare(b.name);
        case "lifecycle_stage": {
          const order = meta?.lifecycle_stages ?? [];
          return (
            order.indexOf(a.lifecycle_stage) - order.indexOf(b.lifecycle_stage) ||
            a.name.localeCompare(b.name)
          );
        }
        default:
          return a.name.localeCompare(b.name);
      }
    };
    return [...filtered].sort(compare);
  }, [pool, stage, klass, priority, owner, query, sort, meta, initiative, bet, betsOf]);

  const owners = [...new Set(pool.map((o) => o.owner_name).filter(Boolean))];

  const header = (key: SortKey, label: string) => (
    <th className="left">
      <button className="linkbtn" onClick={() => setSort(key)} aria-pressed={sort === key}>
        {label}
        {sort === key ? " ↓" : ""}
      </button>
    </th>
  );

  const scaleLabel = dimensions.find((d) => d.key === scale)?.label ?? "";

  return (
    <>
      <ScaleDialog dimension={scale} label={scaleLabel} onClose={() => setScale(null)} />

      <div className="filters">
        <MultiSelect
          label="Class"
          options={classes}
          selected={klass ?? []}
          onChange={setKlass}
        />
        <MultiSelect
          label="Stage"
          options={meta?.lifecycle_stages ?? []}
          selected={stage ?? []}
          onChange={setStage}
        />
        <MultiSelect
          label="Priority"
          options={meta?.priorities ?? []}
          selected={priority ?? []}
          onChange={setPriority}
        />
        <label>
          Owner
          <select value={owner} onChange={(e) => setOwner(e.target.value)}>
            <option value="">Any</option>
            <option value="unassigned">Unassigned</option>
            {owners.map((o) => (
              <option key={o}>{o}</option>
            ))}
          </select>
        </label>
        <label>
          Initiative
          <select value={initiative} onChange={(e) => setInitiative(e.target.value)}>
            <option value="">Any</option>
            <option value={NO_INITIATIVE}>No initiative</option>
            {initiatives.map((i) => (
              <option key={i.id} value={i.id}>
                {i.code} — {i.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Bet
          <select value={bet} onChange={(e) => setBet(e.target.value)}>
            <option value="">Any</option>
            <option value={NO_INITIATIVE}>No bet</option>
            {bets.map((b) => (
              <option key={b.id} value={b.id}>
                {b.code} — {b.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Search
          <input
            type="text"
            value={query}
            placeholder="name contains…"
            onChange={(e) => setQuery(e.target.value)}
          />
        </label>
        <span className="count">
          {rows.length} of {pool.length}
        </span>
        {canWrite && (
          <a className="btn primary create" href={href.create(kind)}>
            Create
          </a>
        )}
      </div>

      <section className="panel">
        <div className="panel-hd">
          <h2>{isService ? "Service maturity" : "Asset maturity"}</h2>
        </div>
        <div className="tablewrap">
          <table className="heat">
            <thead>
              <tr>
                {header("name", "Name")}
                {header("class", "Class")}
                {header("lifecycle_stage", "Stage")}
                {header("owner", "Owner")}
                {header("priority", "Pri")}
                {dimensions.map((d) => (
                  <th key={d.key} className="dim">
                    <button
                      className="dimbtn"
                      title={hintFor(d.key, d.label, kind)}
                      onClick={() => setScale(d.key)}
                    >
                      <DimensionIcon dimension={d.key} />
                      <span className="sr">{d.label} — open the scoring scale</span>
                    </button>
                  </th>
                ))}
                <th style={{ width: 62 }}>
                  <button className="linkbtn" onClick={() => setSort("maturity")}>
                    Score{sort === "maturity" ? " ↓" : ""}
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((o) => (
                <tr key={o.id}>
                  <td className="name left">
                    <a href={href.object(o.kind, o.id)}>{o.name}</a>
                    <InitiativeChip initiative={o.initiative} />
                    {o.initiative_id && (
                      <BetChips betIds={betsOf.get(o.initiative_id) ?? []} />
                    )}
                  </td>
                  <td className="left muted">{o.class}</td>
                  <td className="left muted">{o.lifecycle_stage}</td>
                  <td className="left muted">{o.owner_name ?? "—"}</td>
                  <td className="left">
                    <Chip text={o.priority} tone={priorityTone(o.priority)} />
                  </td>
                  {dimensions.map((d) => {
                    const value = o.scores[d.key];
                    return (
                      <td key={d.key} className="cell">
                        <div
                          style={scoreStyle(value)}
                          title={`${d.label}: ${value ?? "not assessed"}`}
                        >
                          <Score value={value} />
                        </div>
                      </td>
                    );
                  })}
                  <td className="score">
                    <Score value={o.maturity.score} />
                    <div className="muted" style={{ fontSize: 10, fontWeight: 400 }}>
                      {o.maturity.assessed}/{o.maturity.of}
                    </div>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td className="left muted" colSpan={6 + dimensions.length}>
                    Nothing matches these filters. Stage starts with Idea switched off — turn it
                    back on if you are looking for something that has not started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}
